// API Configuration
const API_BASE_URL = 'https://app.yodyphuonganh.vn/api';
const CUNGVEDICH_API_BASE_URL = process.env.REACT_APP_CUNGVEDICH_API_URL || 'https://api.cungvedich.vn/api';
const ORGANIZATION_ID = 4;

// Timeout phân tầng theo độ nặng request — endpoint nặng cần lâu hơn để tránh false-timeout
export const TIMEOUTS = {
  fast: 10000,     // lookup nhẹ (provinces, dropdown)
  default: 25000,  // request thường
  heavy: 45000,    // inventory list, sale-report-yearly (BE join nhiều bảng)
  upload: 60000,   // upload ảnh
};

// ApiError thay thế Error generic — giữ kind để UI phân biệt timeout vs network vs server
// Trước đây mọi error đều quy về "Không thể kết nối đến server" → user/dev không biết nguyên nhân
export class ApiError extends Error {
  constructor(message, { kind = 'unknown', status, retryable = false, cause } = {}) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind; // 'timeout' | 'network' | 'server' | 'client' | 'auth' | 'unknown'
    this.status = status;
    this.retryable = retryable;
    this.cause = cause;
  }
  static fromFetchError(err) {
    if (err?.name === 'AbortError') {
      return new ApiError('Mạng chậm, hãy thử lại', { kind: 'timeout', retryable: true, cause: err });
    }
    if (err instanceof TypeError) {
      return new ApiError('Mất kết nối, kiểm tra WiFi/4G', { kind: 'network', retryable: true, cause: err });
    }
    return new ApiError(err?.message || 'Lỗi không xác định', { kind: 'unknown', retryable: false, cause: err });
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const RETRY_DELAYS_MS = [500, 1500, 3000];

// fetchWithTimeout: giữ tên cũ để không phá call site, nhưng bên trong là retry + ApiError
// options.timeout: number — override default
// options.noRetry: true — force không retry (đã default cho non-GET để an toàn idempotency)
// Auto: chỉ retry với GET (idempotent). POST/PUT/DELETE/PATCH không retry — tránh 2 lần POST = 2 record.
export const fetchWithTimeout = async (url, options = {}, legacyTimeout) => {
  const { timeout: optTimeout, noRetry, ...fetchOpts } = options;
  const timeoutMs = legacyTimeout || optTimeout || TIMEOUTS.default;
  const method = (fetchOpts.method || 'GET').toUpperCase();
  const isSafeMethod = method === 'GET' || method === 'HEAD';
  const maxAttempts = (noRetry || !isSafeMethod) ? 1 : RETRY_DELAYS_MS.length + 1;
  let lastError;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...fetchOpts, signal: controller.signal });
      clearTimeout(timeoutId);
      // 5xx coi như tạm thời → retry. 4xx không retry (client error, retry vô nghĩa).
      if (response.status >= 500 && response.status < 600 && attempt + 1 < maxAttempts) {
        lastError = new ApiError(`Hệ thống đang bận (${response.status})`, {
          kind: 'server', status: response.status, retryable: true,
        });
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      const apiErr = err instanceof ApiError ? err : ApiError.fromFetchError(err);
      lastError = apiErr;
      if (!apiErr.retryable || attempt + 1 >= maxAttempts) {
        throw apiErr;
      }
      await sleep(RETRY_DELAYS_MS[attempt]);
    }
  }
  throw lastError;
};

// Cung Ve Dich API functions
const CungVeDichAPI = {
  // Get customer list for agent
  getCustomerList: async (accessToken) => {
    try {
      console.log('Getting customer list with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for customer list API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/redeem/redeem/team-members-pending`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Customer list response:', data);

        // Transform data to match expected format
        // API returns: [{id, name, phone, pendingCount}]
        const customers = data.data.map(customer => ({
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          pendingStamps: customer.pendingCount || 0,
          totalStamps: customer.totalCount || 0
        }));

        return {
          success: true,
          customers: customers,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get customer list error:', errorData);
        throw new Error(`Không thể lấy danh sách khách hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get customer list network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách khách hàng.');
    }
  },

  // Get customer pending stamps detail
  getCustomerPendingStamps: async (customerId, accessToken) => {
    try {
      console.log('Getting pending stamps for customer:', customerId);

      if (!accessToken) {
        throw new Error('Access token is required for pending stamps API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/redeem/redeem/team-redeemed-codes?redeemedBy=${customerId}`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Pending stamps response:', data);

        // Transform data to match expected format
        // API returns: { statusCode: 200, data: { count: 0, rows: [{ scratchCode, serialNumber, productName, price, status }] } }
        const stampsData = data.data?.rows || [];

        // Transform status: 1=chờ duyệt, 2=đã duyệt, 3=từ chối
        const transformedStamps = stampsData.map(stamp => ({
          id: stamp.scratchCode || stamp.serialNumber,
          code: stamp.scratchCode || stamp.serialNumber,
          productName: stamp.productName,
          quantity: 1, // Default quantity since API doesn't provide this
          price: stamp.price,
          status: stamp.status === 1 ? 'pending' :
            stamp.status === 2 ? 'approved' : 'rejected',
          createdAt: new Date().toISOString() // Default to current time since API doesn't provide this
        }));

        return {
          success: true,
          stamps: transformedStamps,
          count: data.data?.count || transformedStamps.length
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get pending stamps error:', errorData);
        throw new Error(`Không thể lấy thông tin mã tem chờ duyệt: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get pending stamps network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin mã tem chờ duyệt.');
    }
  },

  // Approve stamp
  approveStamp: async (stampId, accessToken) => {
    try {
      console.log('Approving stamp:', stampId);

      if (!accessToken) {
        throw new Error('Access token is required for stamp approval API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/redeem/redeem/${stampId}/status`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 2
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stamp approval response:', data);

        return {
          success: true,
          message: 'Duyệt mã tem thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Stamp approval error:', errorData);
        throw new Error(`Không thể duyệt mã tem: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Stamp approval network error:', error);
      throw new Error('Không thể kết nối đến server để duyệt mã tem.');
    }
  },

  // Reject stamp
  rejectStamp: async (stampId, accessToken, reason = '') => {
    try {
      console.log('Rejecting stamp:', stampId, 'with reason:', reason);

      if (!accessToken) {
        throw new Error('Access token is required for stamp rejection API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/redeem/redeem/${stampId}/status`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 3
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stamp rejection response:', data);

        return {
          success: true,
          message: 'Từ chối mã tem thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Stamp rejection error:', errorData);
        throw new Error(`Không thể từ chối mã tem: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Stamp rejection network error:', error);
      throw new Error('Không thể kết nối đến server để từ chối mã tem.');
    }
  },

  // Get product inventory list
  // Endpoint nặng — BE join product + inventory + price → cần TIMEOUTS.heavy (45s). Trước dùng 20s default bị false-timeout.
  getProductInventory: async (accessToken) => {
    if (!accessToken) {
      throw new ApiError('Phiên đăng nhập hết hạn, vui lòng đăng nhập lại', { kind: 'auth' });
    }
    try {
      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/products/product-inventory`, {
        method: 'GET',
        headers: { 'accept': '*/*', 'x-access-token': accessToken },
        timeout: TIMEOUTS.heavy,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `Không thể lấy danh sách sản phẩm trong kho (${response.status})`,
          { kind: response.status >= 500 ? 'server' : 'client', status: response.status }
        );
      }
      const data = await response.json();
      const products = (data.data || []).map(product => ({
        id: product.productId,
        name: product.productName,
        code: product.productCode,
        price: product.price || product.productPrice,
        description: product.productDescription,
        imageUrl: product.productImageUrl,
        color: product.productColor,
        ingredient: product.ingredient,
        target: product.target,
        type: product.type,
        age: product.age,
        style: product.style,
        stock: product.stock,
        status: product.status,
        quantity: product.quantity || 0,
        priceTable: product.priceTable || []
      }));
      return { success: true, products, data };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(error?.message || 'Không thể tải danh sách kho', { kind: 'unknown', cause: error });
    }
  },

  // Submit inventory update
  // noRetry=true: PUT mutate, idempotency-safe (2 lần PUT = ghi đè giống nhau nhưng vẫn nên tránh duplicate request)
  submitInventoryUpdate: async (inventoryData, accessToken) => {
    if (!accessToken) {
      throw new ApiError('Phiên đăng nhập hết hạn, vui lòng đăng nhập lại', { kind: 'auth' });
    }
    try {
      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/products/product-inventory`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ inventories: inventoryData }),
        noRetry: true,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `Không thể cập nhật kho (${response.status})`,
          { kind: response.status >= 500 ? 'server' : 'client', status: response.status }
        );
      }
      const data = await response.json();
      return { success: true, message: 'Cập nhật kho thành công', data };
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(error?.message || 'Không thể cập nhật kho', { kind: 'unknown', cause: error });
    }
  },

  // Get sales report data for specific month/year
  getSalesReport: async (month, year, accessToken) => {
    try {
      console.log('Getting sales report for month:', month, 'year:', year);

      if (!accessToken) {
        throw new Error('Access token is required for sales report API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/products/product-sale-report?month=${month}&year=${year}`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Sales report response:', data);

        // Transform data to match expected format
        const salesData = data.data || [];

        return {
          success: true,
          salesData: salesData,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get sales report error:', errorData);
        throw new Error(`Không thể lấy báo cáo doanh số: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get sales report network error:', error);
      throw new Error('Không thể kết nối đến server để lấy báo cáo doanh số.');
    }
  },

  // Get products list (fallback when no sales data exists)
  getProducts: async (accessToken) => {
    try {
      console.log('Getting products list with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for products API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/products?page=1&pageSize=1000`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Products response:', data);

        // Transform data to match expected format
        const products = data.data?.rows || [];

        return {
          success: true,
          products: products,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get products error:', errorData);
        throw new Error(`Không thể lấy danh sách sản phẩm: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get products network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách sản phẩm.');
    }
  },

  // Submit sales report
  submitSalesReport: async (salesData, accessToken) => {
    try {
      console.log('Submitting sales report with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for sales report submission API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/products/product-sale-report`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(salesData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Sales report submission response:', data);

        return {
          success: true,
          message: 'Lưu báo cáo doanh số thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Sales report submission error:', errorData);
        throw new Error(`Không thể lưu báo cáo doanh số: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Sales report submission network error:', error);
      throw new Error('Không thể kết nối đến server để lưu báo cáo doanh số.');
    }
  },

  // Get shipment reports list
  getShipmentReports: async (params = {}, accessToken) => {
    try {
      console.log('Getting shipment reports with params:', params);

      if (!accessToken) {
        throw new Error('Access token is required for shipment reports API');
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.pageSize) queryParams.append('pageSize', params.pageSize);
      if (params.status !== undefined) queryParams.append('status', params.status);
      if (params.textSearch) queryParams.append('textSearch', params.textSearch);
      if (params.sortField) queryParams.append('sortField', params.sortField);
      if (params.sortType) queryParams.append('sortType', params.sortType);

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/shipment-reports?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Shipment reports response:', data);

        // Handle nested data structure
        const responseData = data.data || data;

        return {
          success: true,
          rows: responseData.rows || [],
          count: responseData.count || 0,
          page: parseInt(responseData.page) || 1,
          pageSize: parseInt(responseData.pageSize) || 10,
          totalPages: responseData.totalPages || 0
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get shipment reports error:', errorData);
        throw new Error(`Không thể lấy danh sách báo cáo: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get shipment reports network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách báo cáo.');
    }
  },

  // Get shipment report detail
  getShipmentReport: async (id, accessToken) => {
    try {
      console.log('Getting shipment report detail for id:', id);

      if (!accessToken) {
        throw new Error('Access token is required for shipment report detail API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/shipment-reports/${id}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Shipment report detail response:', data);

        // Handle nested data structure
        const reportData = data.data || data;

        return {
          success: true,
          data: reportData
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get shipment report detail error:', errorData);
        throw new Error(`Không thể lấy chi tiết báo cáo: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get shipment report detail network error:', error);
      throw new Error('Không thể kết nối đến server để lấy chi tiết báo cáo.');
    }
  },

  // Create shipment report
  createShipmentReport: async (reportData, accessToken) => {
    try {
      console.log('Creating shipment report with data:', reportData);

      if (!accessToken) {
        throw new Error('Access token is required for create shipment report API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/shipment-reports`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Create shipment report response:', data);

        // Handle nested data structure
        const reportData = data.data || data;

        return {
          success: true,
          message: 'Tạo phiếu trả hàng thành công',
          data: reportData
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create shipment report error:', errorData);
        throw new Error(`Không thể tạo báo cáo: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Create shipment report network error:', error);
      throw new Error('Không thể kết nối đến server để tạo báo cáo.');
    }
  },

  // Update shipment report
  updateShipmentReport: async (id, reportData, accessToken) => {
    try {
      console.log('Updating shipment report id:', id, 'with data:', reportData);

      if (!accessToken) {
        throw new Error('Access token is required for update shipment report API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/shipment-reports/${id}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Update shipment report response:', data);

        // Handle nested data structure
        const reportData = data.data || data;

        return {
          success: true,
          message: 'Cập nhật phiếu trả hàng thành công',
          data: reportData
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update shipment report error:', errorData);
        throw new Error(`Không thể cập nhật báo cáo: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Update shipment report network error:', error);
      throw new Error('Không thể kết nối đến server để cập nhật báo cáo.');
    }
  },

  // Delete shipment report
  deleteShipmentReport: async (id, accessToken) => {
    try {
      console.log('Deleting shipment report id:', id);

      if (!accessToken) {
        throw new Error('Access token is required for delete shipment report API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/shipment-reports/${id}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Delete shipment report response:', data);

        return {
          success: true,
          message: 'Xóa phiếu trả hàng thành công',
          data: data.data || data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete shipment report error:', errorData);
        throw new Error(`Không thể xóa báo cáo: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Delete shipment report network error:', error);
      throw new Error('Không thể kết nối đến server để xóa báo cáo.');
    }
  },

  // ==================== FEATURE CONFIG APIs ====================

  /**
   * Check features for current logged-in user
   * This is the MAIN API - called on every page load/refresh
   * Returns list of features with isAccess flag based on user's data conditions
   */
  checkMyFeatures: async (accessToken) => {
    try {
      console.log('🔍 Checking my features with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for feature check API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config/check/my-features`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Feature check response:', data);

        // API returns array directly or wrapped in data
        const features = Array.isArray(data) ? data : (data.data || []);

        return {
          success: true,
          features: features,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Feature check error:', errorData);
        throw new Error(`Không thể kiểm tra quyền truy cập: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('❌ Feature check network error:', error);
      throw new Error('Không thể kết nối đến server để kiểm tra quyền truy cập.');
    }
  },

  /**
   * Get list of available condition types (for admin UI)
   * Returns metadata about conditions that can be used to configure features
   */
  getConditionTypes: async (accessToken) => {
    try {
      console.log('Getting condition types with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for condition types API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config/condition-types/list`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Condition types response:', data);

        const conditionTypes = Array.isArray(data) ? data : (data.data || []);

        return {
          success: true,
          conditionTypes: conditionTypes,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get condition types error:', errorData);
        throw new Error(`Không thể lấy danh sách điều kiện: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get condition types network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách điều kiện.');
    }
  },

  /**
   * Create new feature config (Admin only)
   */
  createFeatureConfig: async (configData, accessToken) => {
    try {
      console.log('Creating feature config with data:', configData);

      if (!accessToken) {
        throw new Error('Access token is required for create feature config API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Create feature config response:', data);

        return {
          success: true,
          message: 'Tạo cấu hình tính năng thành công',
          data: data.data || data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create feature config error:', errorData);
        throw new Error(`Không thể tạo cấu hình: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Create feature config network error:', error);
      throw new Error('Không thể kết nối đến server để tạo cấu hình.');
    }
  },

  /**
   * Update existing feature config (Admin only)
   */
  updateFeatureConfig: async (id, configData, accessToken) => {
    try {
      console.log('Updating feature config id:', id, 'with data:', configData);

      if (!accessToken) {
        throw new Error('Access token is required for update feature config API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config/${id}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Update feature config response:', data);

        return {
          success: true,
          message: 'Cập nhật cấu hình tính năng thành công',
          data: data.data || data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update feature config error:', errorData);
        throw new Error(`Không thể cập nhật cấu hình: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Update feature config network error:', error);
      throw new Error('Không thể kết nối đến server để cập nhật cấu hình.');
    }
  },

  /**
   * Delete feature config (Admin only, soft delete)
   */
  deleteFeatureConfig: async (id, accessToken) => {
    try {
      console.log('Deleting feature config id:', id);

      if (!accessToken) {
        throw new Error('Access token is required for delete feature config API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config/${id}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Delete feature config response:', data);

        return {
          success: true,
          message: 'Xóa cấu hình tính năng thành công',
          data: data.data || data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete feature config error:', errorData);
        throw new Error(`Không thể xóa cấu hình: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Delete feature config network error:', error);
      throw new Error('Không thể kết nối đến server để xóa cấu hình.');
    }
  },

  /**
   * Get list of all feature configs (Admin only)
   */
  getFeatureConfigs: async (queryParams = {}, accessToken) => {
    try {
      console.log('Getting feature configs with params:', queryParams);

      if (!accessToken) {
        throw new Error('Access token is required for feature configs list API');
      }

      const query = new URLSearchParams();
      if (queryParams.page) query.append('page', queryParams.page);
      if (queryParams.pageSize) query.append('pageSize', queryParams.pageSize);
      if (queryParams.featureKey) query.append('featureKey', queryParams.featureKey);
      if (queryParams.isActive !== undefined) query.append('isActive', queryParams.isActive);

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config?${query.toString()}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Feature configs response:', data);

        const responseData = data.data || data;

        return {
          success: true,
          configs: responseData.data || responseData,
          total: responseData.total || 0,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get feature configs error:', errorData);
        throw new Error(`Không thể lấy danh sách cấu hình: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get feature configs network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách cấu hình.');
    }
  },

  /**
   * Get feature config by ID (Admin only)
   */
  getFeatureConfig: async (id, accessToken) => {
    try {
      console.log('Getting feature config by id:', id);

      if (!accessToken) {
        throw new Error('Access token is required for feature config detail API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/feature-config/${id}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Feature config detail response:', data);

        return {
          success: true,
          config: data.data || data,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get feature config error:', errorData);
        throw new Error(`Không thể lấy chi tiết cấu hình: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get feature config network error:', error);
      throw new Error('Không thể kết nối đến server để lấy chi tiết cấu hình.');
    }
  },

  /**
   * Get orders list
   * @param {string} accessToken - Access token for authentication
   * @param {number} userId - User ID to filter orders
   * @param {number} type - Optional order type (1=retail, 2=wholesale)
   * @param {object} params - Optional params (startDate, endDate)
   */
  getOrders: async (accessToken, userId, type = null, params = {}) => {
    try {
      console.log('Getting orders with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None', 'type:', type);

      if (!accessToken) {
        throw new Error('Access token is required for orders API');
      }

      // Build query string with optional type parameter
      let queryString = `createdById=${userId}`;
      if (type !== null && type !== undefined) {
        queryString += `&type=${type}`;
      }

      // Add optional date filtering if provided in params or separate args
      // We check if the 3rd or 4th argument is an object (params) or if specific date args are passed
      // But based on current usage, cleaner way is to expect params object as 4th arg or just handle it here if passed
      // Since function signature is (accessToken, userId, type), I will check if type contains params or add new args
      // Actually, let's keep it simple and safe: extend the function signature carefully or check if 'type' is being used as params object in future refactors. 
      // For now, let's assume valid date parameters might be passed in a way we can consume.
      // Wait, I should stick to the plan: "Update getOrders to accept an optional params object (or specific date arguments)".

      // Better approach: Let's modify the signature to `getOrders: async (accessToken, userId, type = null, params = {})`
      // But wait, existing calls might break if I change signature too much or if I don't handle it right.

      // Let's look at `getOrders: async (accessToken, userId, type = null) => {`
      // I will change it to `getOrders: async (accessToken, userId, type = null, params = {}) => {`

      if (typeof params === 'object' && params !== null) {
        if (params.startDate) queryString += `&startDate=${params.startDate}`;
        if (params.endDate) queryString += `&endDate=${params.endDate}`;
        if (params.pageSize) queryString += `&pageSize=${params.pageSize}`;
        if (params.customerId) queryString += `&customerId=${params.customerId}`;
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/orders?${queryString}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Orders response:', responseData);

        // Transform data to match expected format
        const orders = responseData.data.rows || [];

        return {
          success: true,
          orders: orders,
          data: responseData.data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get orders error:', errorData);
        throw new Error(`Không thể lấy danh sách đơn hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get orders network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách đơn hàng.');
    }
  },

  /**
   * Get order by ID
   */
  getOrder: async (id, accessToken) => {
    try {
      console.log('Getting order by id:', id);

      if (!accessToken) {
        throw new Error('Access token is required for order detail API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/orders/${id}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Order detail response:', data);

        return {
          success: true,
          order: data.data || data,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get order error:', errorData);
        throw new Error(`Không thể lấy chi tiết đơn hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get order network error:', error);
      throw new Error('Không thể kết nối đến server để lấy chi tiết đơn hàng.');
    }
  },

  /**
   * Get order by ID - Public (no authentication required)
   */
  getOrderPublic: async (id) => {
    try {
      console.log('Getting order by id (public):', id);
      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/orders/${id}/public`, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Order detail public response:', data);

        return {
          success: true,
          order: data.data || data,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get order public error:', errorData);
        throw new Error(`Không thể lấy chi tiết đơn hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get order public network error:', error);
      throw new Error('Không thể kết nối đến server để lấy chi tiết đơn hàng.');
    }
  },

  /**
   * Create new order
   */
  createOrder: async (orderData, accessToken) => {
    try {
      console.log('Creating order with data:', orderData);

      if (!accessToken) {
        throw new Error('Access token is required for create order API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/orders`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('=== CREATE ORDER API RESPONSE ===');
        console.log('Raw response data:', data);
        console.log('data.statusCode:', data.statusCode);
        console.log('data.data:', data.data);
        console.log('data.data.id:', data.data?.id);
        console.log('Type of data.data:', typeof data.data);
        console.log('Full JSON:', JSON.stringify(data, null, 2));

        const result = {
          success: true,
          message: 'Tạo đơn hàng thành công',
          order: data.data || data,
          data: data.data || data
        };

        console.log('=== RETURN OBJECT ===');
        console.log('result:', result);
        console.log('result.data:', result.data);
        console.log('result.data.id:', result.data?.id);

        return result;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create order error:', errorData);
        throw new Error(`Không thể tạo đơn hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Create order network error:', error);
      throw new Error('Không thể kết nối đến server để tạo đơn hàng.');
    }
  },

  /**
   * Update order
   */
  updateOrder: async (id, orderData, accessToken) => {
    try {
      console.log('Updating order id:', id, 'with data:', orderData);

      if (!accessToken) {
        throw new Error('Access token is required for update order API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/orders/${id}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Update order response:', data);

        return {
          success: true,
          message: 'Cập nhật đơn hàng thành công',
          order: data.data || data,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update order error:', errorData);
        throw new Error(`Không thể cập nhật đơn hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Update order network error:', error);
      throw new Error('Không thể kết nối đến server để cập nhật đơn hàng.');
    }
  },

  /**
   * Delete order
   */
  deleteOrder: async (id, accessToken) => {
    try {
      console.log('Deleting order id:', id);

      if (!accessToken) {
        throw new Error('Access token is required for delete order API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/orders/${id}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Delete order response:', data);

        return {
          success: true,
          message: 'Xóa đơn hàng thành công',
          data: data.data || data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete order error:', errorData);
        throw new Error(`Không thể xóa đơn hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Delete order network error:', error);
      throw new Error('Không thể kết nối đến server để xóa đơn hàng.');
    }
  },

  /**
   * Get customers list
   */
  getCustomers: async (accessToken, parentId, roleId = 15) => {
    try {
      console.log('Getting customers with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for customers API');
      }

      // BE mặc định chỉ 100 dòng → đại lý >100 khách (nhiều nhất 1.225) bị thiếu khách trong ô chọn
      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users?parentId=${parentId}&roleId=${roleId}&pageSize=2000`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Customers response:', data);
        return {
          success: true,
          customers: data.data.rows,
          data: data.data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get customers error:', errorData);
        throw new Error(`Không thể lấy danh sách khách hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get customers network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách khách hàng.');
    }
  },

  /**
   * Create new customer
   */
  createCustomer: async (customerData, accessToken) => {
    try {
      console.log('Creating customer with data:', customerData);

      if (!accessToken) {
        throw new Error('Access token is required for create customer API');
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/customers`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: customerData.facebook || customerData.name,
          phone: customerData.phoneNumber,
          facebook: customerData.facebook || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Create customer response:', data);

        return {
          success: true,
          message: 'Thêm khách hàng thành công',
          customer: data.data || data,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create customer error:', errorData);
        throw new Error(`Không thể thêm khách hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Create customer network error:', error);
      throw new Error('Khách hàng với SĐT này đã tồn tại trong hệ thống');
    }
  }
};

// API functions with real endpoints
const API = {
  // Check if user exists by phone number and return user info
  checkUserExists: async (phoneNumber, organizationId = null) => {
    try {
      console.log('Checking user exists for phone:', phoneNumber, 'with organizationId:', organizationId || ORGANIZATION_ID);

      const response = await fetchWithTimeout(`${API_BASE_URL}/auth/login-with-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          organizationId: organizationId || ORGANIZATION_ID
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        // 2xx status codes - User exists
        try {
          const data = await response.json();
          console.log('User exists - Response data:', data);

          // Extract accessToken if available - check multiple possible locations
          const accessToken = data.accessToken ||
            data.token ||
            data.access_token ||
            (data.data && data.data.accessToken) ||
            (data.data && data.data.token) ||
            (data.data && data.data.access_token) ||
            null;

          console.log('🔍 API.checkUserExists - AccessToken extraction:', {
            'data.accessToken': data.accessToken,
            'data.data?.accessToken': data.data?.accessToken,
            'extracted accessToken': accessToken ? accessToken.substring(0, 20) + '...' : 'None'
          });

          // Return user info if available, otherwise just true
          if (data && (data.user || data.data)) {
            return {
              exists: true,
              userInfo: data.user || data.data || null,
              accessToken: accessToken
            };
          }
          return {
            exists: true,
            userInfo: null,
            accessToken: accessToken
          };
        } catch (parseError) {
          console.warn('Could not parse JSON for 2xx response, but user exists:', parseError);
          return {
            exists: true,
            userInfo: null,
            accessToken: null
          };
        }
      } else if (response.status === 404 || response.status === 401) {
        // 401 có thể là ACCOUNT_LOCKED / PARENT_LOCKED — parse errorCode trước khi giả định "user not found"
        const errorData = await response.json().catch(() => ({}));
        const errorCode = errorData.errorCode;
        if (['ACCOUNT_LOCKED', 'PARENT_LOCKED'].includes(errorCode)) {
          return { exists: false, errorCode, detail: errorData.detail || null };
        }
        const errorMessage = `API Error (${response.status}): User not found`;
        throw new Error(errorMessage);
      } else {
        // Other error - try to get error details
        console.log('Other error status:', response.status);
        try {
          const errorData = await response.json();
          console.error('API Error data:', errorData);
          throw new Error(`API Error (${response.status}): ${errorData.message || 'Có lỗi xảy ra khi kiểm tra số điện thoại'}`);
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
          throw new Error(`API Error (${response.status}): Có lỗi xảy ra khi kiểm tra số điện thoại`);
        }
      }
    } catch (error) {
      // Only network errors should reach here now
      if (error.message.includes('API Error')) {
        // Re-throw API errors
        throw error;
      } else {
        console.error('Network error:', error);
        throw new Error('Không thể kết nối đến server. Vui lòng thử lại.');
      }
    }
  },

  // Login with password via CungVeDich API - for requireLogin users
  // Returns: {success, userInfo, accessToken} on success, or {errorCode} for password errors
  login: async (phoneNumber, password, organizationId = null) => {
    try {
      const body = {
        username: phoneNumber,
        organizationId: `${organizationId || ORGANIZATION_ID}`
      };
      if (password) {
        body.password = password;
      }

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        const accessToken = data.accessToken || data.data?.accessToken || null;
        return {
          success: true,
          userInfo: data.user || data.data || null,
          accessToken: accessToken
        };
      }

      // Parse error response for known error codes (password + lock)
      const errorData = await response.json().catch(() => ({}));
      const errorCode = errorData.errorCode;

      if (['PASSWORD_REQUIRED', 'PASSWORD_NOT_SET', 'INVALID_PASSWORD'].includes(errorCode)) {
        return { success: false, errorCode };
      }
      // ACCOUNT_LOCKED / PARENT_LOCKED — trả về detail để FE render màn Lock-screen với info đầy đủ
      if (['ACCOUNT_LOCKED', 'PARENT_LOCKED'].includes(errorCode)) {
        return { success: false, errorCode, detail: errorData.detail || null };
      }

      throw new Error(`API Error (${response.status}): ${errorData.message || 'Đăng nhập thất bại'}`);
    } catch (error) {
      if (error.message.includes('API Error')) throw error;
      throw new Error('Không thể kết nối đến server để đăng nhập.');
    }
  },

  // Set initial password for requireLogin users (public, no auth needed)
  changePasswordPublic: async (phoneNumber, newPassword, organizationId = null) => {
    try {
      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: phoneNumber,
          newPassword: newPassword,
          organizationId: organizationId || ORGANIZATION_ID
        })
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Không thể đặt mật khẩu');
      }
    } catch (error) {
      if (error.message.includes('API Error') || error.message.includes('Không thể')) {
        throw error;
      }
      throw new Error('Không thể kết nối đến server để đặt mật khẩu.');
    }
  },

  // Get detailed user information by phone number
  getUserInfo: async (phoneNumber) => {
    try {
      console.log('Getting user info for phone:', phoneNumber);

      const response = await fetchWithTimeout(`${API_BASE_URL}/users/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User info response:', data);
        return data.user || data.data || data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get user info error:', errorData);
        throw new Error(`Không thể lấy thông tin người dùng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get user info network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin người dùng.');
    }
  },

  // Update user profile (e.g., set teamLeader) with access token
  updateProfile: async (accessToken, profileData) => {
    try {
      if (!accessToken) throw new Error('Access token is required');
      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-token': accessToken },
        body: JSON.stringify(profileData),
      });
      if (response.ok) {
        const data = await response.json();
        return data.data ?? data;
      }
      throw new Error('Không thể cập nhật thông tin');
    } catch (error) {
      throw new Error(error.message || 'Không thể kết nối đến server');
    }
  },

  // Get user profile with access token
  getUserProfile: async (accessToken) => {
    try {
      console.log('Getting user profile with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for user profile API');
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}/users/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User profile response:', data);

        // Extract user data from response - handle the actual API structure
        const userData = data.userData || data.data || data.user || data;

        return {
          success: true,
          user: userData,
          userData: userData, // Also include userData for compatibility
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get user profile error:', errorData);
        throw new Error(`Không thể lấy thông tin người dùng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get user profile network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin người dùng.');
    }
  },

  // Upload ảnh đại diện — dùng /system/file (NoAuth) trên CungVeDich API.
  // KHÔNG dùng API_BASE_URL/documents/file (Yody cũ, cần auth → timeout khi đăng ký).
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/system/file`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // TransformInterceptor: { statusCode, data: { fileUrl, id, fileName, ... } }
        const inner = data.data || data;
        const fileUrl = inner.fileUrl;
        if (!fileUrl) throw new Error('Upload response format error');
        return { success: true, fileUrl, fileId: inner.id, fileName: inner.fileName || file.name };
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Upload failed (${response.status}): ${errorData.message || 'Không thể upload file. Vui lòng thử lại.'}`);
      }
    } catch (error) {
      throw new Error('Có lỗi xảy ra khi upload file: ' + error.message);
    }
  },

  // Register new user
  registerUser: async (userData) => {
    try {
      // Prepare registration data according to new API specification
      const registrationData = {
        phone: userData.phoneNumber,
        firstName: userData.fullName ? userData.fullName.split(' ').slice(0, -1).join(' ') : '',
        lastName: userData.fullName ? userData.fullName.split(' ').slice(-1)[0] : userData.fullName || '',
        email: null, // Optional field
        organizationId: userData.organizationId || ORGANIZATION_ID,
        address: null, // Optional field
        avatar: userData.avatar || null,
        f1Count: parseInt(userData.f1Count) || 0,
        teamLeader: userData.supervisorPhone || null,
        phoneParent: userData.supervisorPhone || null,
        nameParent: userData.supervisorName || null,
        level: parseInt(userData.fLevel) || 1,
        birthday: userData.birthDate ? new Date(userData.birthDate).toISOString() : null,
        // Additional MLM structure fields
        f1Team: userData.f1Team || null,
        supervisorName: userData.supervisorName || null,
        supervisorPhone: userData.supervisorPhone || null,
        facebook: userData.facebook || null
      };

      console.log('Registering user with data:', registrationData);

      const response = await fetchWithTimeout(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      if (response.ok) {
        // 2xx status codes - Success
        const data = await response.json();
        console.log('Registration successful:', data);

        // Extract user info from response
        const userInfo = data.data || data;

        return {
          success: true,
          userId: userInfo?.id || Date.now(),
          user: userInfo,
          userData: data,
          message: 'Đăng ký thành công'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Registration error:', errorData);
        throw new Error(`Registration failed (${response.status}): ${errorData.message || 'Đăng ký thất bại'}`);
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error('Có lỗi xảy ra khi đăng ký: ' + error.message);
    }
  },

  // ===== Đăng ký / cập nhật đại lý mới (mlm-core-service / CungVeDich) =====
  // Tất cả hit CUNGVEDICH_API_BASE_URL — KHÔNG phải Yody legacy (app.yodyphuonganh.vn).

  // Kiểm tra SĐT → {exists, isAgent, approvalStatus, status, approvalNote}.
  // Trả null nếu KHÔNG kiểm tra được (BE chưa có endpoint / lỗi mạng) → FE phải BỎ QUA gate,
  // đăng nhập bình thường (gate không được là điểm chết chặn mọi login).
  checkAgent: async (phone) => {
    try {
      const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/check-agent?phone=${encodeURIComponent(phone)}`, {
        method: 'GET', headers: { 'accept': '*/*' }
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      return data.data || data;
    } catch {
      return null;
    }
  },

  // Tra cấp trên + thủ lĩnh theo SĐT cấp trên → {found, parentUserName, parentCodeFacebook, leaderCodeFacebook}
  getUplineInfo: async (phone) => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/upline-info?phone=${encodeURIComponent(phone)}`, {
      method: 'GET', headers: { 'accept': '*/*' }
    });
    const data = await res.json().catch(() => ({}));
    return data.data || data;
  },

  // Đăng ký đại lý mới (chờ duyệt) → trả hồ sơ {code, level, teamLeader, parentCodeFacebook, leaderCodeFacebook, ...}
  registerAgent: async (payload) => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/register-agent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Đăng ký thất bại');
    return data.data || data;
  },

  // Danh sách tỉnh thành (bảng city)
  getCities: async (search = '') => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/system/cities?pageSize=100${search ? `&search=${encodeURIComponent(search)}` : ''}`, {
      method: 'GET', headers: { 'accept': '*/*' }
    });
    const data = await res.json().catch(() => ({}));
    const payload = data.data || data;
    return payload.rows || payload || [];
  },

  // Trường bắt buộc khi đăng nhập — admin cấu hình (NoAuth, không cần token)
  getLoginRequiredFields: async () => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/login-required-fields`, { method: 'GET' });
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data.data) ? data.data : [];
  },

  // Hồ sơ đại lý hiện tại (đổ form Cập nhật + phát hiện trường thiếu) — cần token
  getMyAgentProfile: async (accessToken) => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/me/agent-profile`, {
      method: 'GET', headers: { 'accept': '*/*', 'x-access-token': accessToken }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Không lấy được hồ sơ');
    return data.data || data;
  },

  // Cập nhật thông tin cá nhân (lưu thẳng, không đụng cấp trên) — cần token
  updateProfileBasic: async (payload, accessToken) => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/profile-basic`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-access-token': accessToken },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Cập nhật thất bại');
    return data.data || data;
  },

  // Yêu cầu đổi cấp trên (chờ admin duyệt) — cần token
  requestChangeParent: async (phoneParent, accessToken) => {
    const res = await fetchWithTimeout(`${CUNGVEDICH_API_BASE_URL}/users/request-change-parent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'x-access-token': accessToken },
      body: JSON.stringify({ phoneParent })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Gửi yêu cầu thất bại');
    return data.data || data;
  },

  // Additional utility methods
  validateImageFile: (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Chỉ chấp nhận file ảnh (JPG, PNG, GIF, WebP)'
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File ảnh không được vượt quá 5MB'
      };
    }

    return {
      isValid: true,
      error: null
    };
  },

  // Check campaign participation eligibility
  checkCampaignEligibility: async (phoneNumber, campaignCode, organizationId = null) => {
    try {
      console.log('Checking campaign eligibility for phone:', phoneNumber, 'campaign:', campaignCode, 'organizationId:', organizationId || ORGANIZATION_ID);

      // Use new API endpoint: GET /api/campaigns/{code}/check-user
      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/${campaignCode}/check-user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign eligibility check response:', data);

        // Check if user exists in campaign participants
        // The API should return whether the user is eligible or not
        const isEligible = data.isEligible || data.eligible || data.exists || data.userExists || false;

        console.log('User eligibility check result:', { isEligible, phoneNumber, data });

        return {
          success: true,
          isEligible: isEligible,
          data: data,
          message: isEligible ? 'User is eligible for campaign' : 'User is not eligible for campaign'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Check campaign eligibility error:', errorData);
        throw new Error(`Không thể kiểm tra điều kiện tham gia: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Check campaign eligibility network error:', error);
      throw new Error('Không thể kết nối đến server để kiểm tra điều kiện tham gia.');
    }
  },

  // Check if user is registered for campaign participation
  checkCampaignParticipation: async (phoneNumber, campaignCode = 'cd2') => {
    try {
      console.log('Checking campaign participation for phone:', phoneNumber, 'campaign:', campaignCode);

      // Use API endpoint: GET /api/campaigns/{code}/check-user?userCode={phoneNumber}
      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/${campaignCode}/check-user?userCode=${phoneNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign participation check response:', data);

        // Check if user exists in campaign participants list
        const isRegistered = data.data?.isExist || false;

        console.log('User participation check result:', { isRegistered, phoneNumber, campaignCode, data });

        return {
          success: true,
          isRegistered: isRegistered,
          data: data,
          message: isRegistered ? 'User is registered for campaign' : 'User is not registered for campaign'
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Check campaign participation error:', errorData);
        throw new Error(`Không thể kiểm tra đăng ký tham gia: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Check campaign participation network error:', error);
      throw new Error('Không thể kết nối đến server để kiểm tra đăng ký tham gia.');
    }
  },

  // Get campaign information by campaign code
  getCampaignInfo: async (campaignCode) => {
    try {
      console.log('Getting campaign info for code:', campaignCode);

      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/${campaignCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign info response:', data);

        // Extract campaign object
        const campaign = data.campaign || data.data || data;

        // Extract URLs from multiple possible locations
        const mainUrl = data.mainUrl || data.main_url || data.formUrl || data.form_url ||
          campaign?.mainUrl || campaign?.main_url || campaign?.formUrl || campaign?.form_url;
        const successUrl = data.successUrl || data.success_url || data.resultUrl || data.result_url ||
          campaign?.successUrl || campaign?.success_url || campaign?.resultUrl || campaign?.result_url;

        console.log('Extracted URLs:', { mainUrl, successUrl, campaign });

        return {
          success: true,
          campaign: campaign,
          mainUrl: mainUrl,
          successUrl: successUrl
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get campaign info error:', errorData);
        throw new Error(`Không thể lấy thông tin campaign: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get campaign info network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin campaign.');
    }
  },

  // Get campaigns for a specific user
  getCampaignsForUser: async (phoneNumber, organizationId = null) => {
    try {
      console.log('Getting campaigns for user phone:', phoneNumber, 'organizationId:', organizationId || ORGANIZATION_ID);

      const response = await fetchWithTimeout(`${API_BASE_URL}/campaigns/user/${phoneNumber}/campaigns?organizationId=${organizationId || ORGANIZATION_ID}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User campaigns response:', data);

        // Extract campaigns array
        const campaigns = data.data || data.campaigns || data || [];

        console.log('Extracted campaigns:', campaigns);

        return {
          success: true,
          campaigns: campaigns,
          statusCode: data.statusCode || 200
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get user campaigns error:', errorData);
        throw new Error(`Không thể lấy danh sách chiến dịch: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get user campaigns network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách chiến dịch.');
    }
  },

  // Get subordinates for a specific user - Returns hierarchical tree structure
  getSubordinates: async (phoneNumber) => {
    try {
      console.log('Getting subordinates for user phone:', phoneNumber);

      const response = await fetchWithTimeout(`${API_BASE_URL}/users/subordinates?userCode=${phoneNumber}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Subordinates response:', data);

        // New API response structure: nested hierarchy with children arrays
        const hierarchyData = data.data?.data?.hierarchy || data.data?.hierarchy || data.hierarchy || [];
        const userInfo = {
          userCode: data.data?.data?.userCode || data.data?.userCode || data.userCode,
          userName: data.data?.data?.userName || data.data?.userName || data.userName,
          level: data.data?.data?.level || data.data?.level || data.level,
          totalDescendants: data.data?.data?.totalDescendants || data.data?.totalDescendants || data.totalDescendants || 0
        };

        // Flatten hierarchy to get all members for grid view
        const flattenHierarchy = (nodes) => {
          let result = [];
          for (const node of nodes) {
            result.push(node);
            if (node.children && node.children.length > 0) {
              result = result.concat(flattenHierarchy(node.children));
            }
          }
          return result;
        };

        const allMembers = flattenHierarchy(hierarchyData);
        const total = userInfo.totalDescendants || allMembers.length;

        console.log('Extracted hierarchical data:', {
          hierarchyData,
          allMembers: allMembers.length,
          total,
          userInfo
        });

        return {
          success: true,
          subordinates: allMembers, // Flattened list for grid view
          hierarchy: hierarchyData, // Tree structure for tree view
          total: total,
          userInfo: userInfo,
          statusCode: data.statusCode || 200
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get subordinates error:', errorData);
        throw new Error(`Không thể lấy danh sách thành viên: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get subordinates network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách thành viên.');
    }
  },

  // Get campaign points detail for authenticated user
  getCampaignPointsDetail: async (accessToken) => {
    try {
      console.log('Getting campaign points detail with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for points API');
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}/users/campaign-points-detail`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Campaign points detail response:', data);

        // Extract points data from response
        const pointsData = data.data || data;
        const totalPoints = pointsData.totalPoints || 0;
        const campaignCount = pointsData.campaignCount || 0;
        const campaignDetails = pointsData.campaignDetails || [];

        return {
          success: true,
          totalPoints: totalPoints,
          campaignCount: campaignCount,
          campaignDetails: campaignDetails,
          data: pointsData
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get campaign points error:', errorData);
        throw new Error(`Không thể lấy thông tin điểm: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get campaign points network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin điểm.');
    }
  },

  // Get customer list for agent
  getCustomerList: async (accessToken) => {
    try {
      console.log('Getting customer list with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for customer list API');
      }

      const response = await fetchWithTimeout('https://api.cungvedich.vn/api/redeem/redeem/team-members-pending', {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Customer list response:', data);

        // Transform data to match expected format
        // API returns: [{id, name, phone, pendingCount}]
        const customers = data.data.map(customer => ({
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          pendingStamps: customer.pendingCount || 0,
          totalStamps: customer.totalCount || 0
        }));

        return {
          success: true,
          customers: customers,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get customer list error:', errorData);
        throw new Error(`Không thể lấy danh sách khách hàng: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get customer list network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách khách hàng.');
    }
  },

  // Get customer pending stamps detail
  getCustomerPendingStamps: async (customerId, accessToken) => {
    try {
      console.log('Getting pending stamps for customer:', customerId);

      if (!accessToken) {
        throw new Error('Access token is required for pending stamps API');
      }

      const response = await fetchWithTimeout(`https://api.cungvedich.vn/api/redeem/redeem/team-redeemed-codes?redeemedBy=${customerId}`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Pending stamps response:', data);

        // Transform data to match expected format
        // API returns: { statusCode: 200, data: { count: 0, rows: [{ scratchCode, serialNumber, productName, price, status }] } }
        const stampsData = data.data?.rows || [];

        // Transform status: 1=chờ duyệt, 2=đã duyệt, 3=từ chối
        const transformedStamps = stampsData.map(stamp => ({
          id: stamp.scratchCode || stamp.serialNumber,
          code: stamp.scratchCode || stamp.serialNumber,
          productName: stamp.productName,
          quantity: 1, // Default quantity since API doesn't provide this
          price: stamp.price,
          status: stamp.status === 1 ? 'pending' :
            stamp.status === 2 ? 'approved' : 'rejected',
          createdAt: new Date().toISOString() // Default to current time since API doesn't provide this
        }));

        return {
          success: true,
          stamps: transformedStamps,
          count: data.data?.count || transformedStamps.length
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get pending stamps error:', errorData);
        throw new Error(`Không thể lấy thông tin mã tem chờ duyệt: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get pending stamps network error:', error);
      throw new Error('Không thể kết nối đến server để lấy thông tin mã tem chờ duyệt.');
    }
  },

  // Approve stamp
  approveStamp: async (stampId, accessToken) => {
    try {
      console.log('Approving stamp:', stampId);

      if (!accessToken) {
        throw new Error('Access token is required for stamp approval API');
      }

      const response = await fetchWithTimeout(`https://api.cungvedich.vn/api/redeem/redeem/${stampId}/status`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 2
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stamp approval response:', data);

        return {
          success: true,
          message: 'Duyệt mã tem thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Stamp approval error:', errorData);
        throw new Error(`Không thể duyệt mã tem: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Stamp approval network error:', error);
      throw new Error('Không thể kết nối đến server để duyệt mã tem.');
    }
  },

  // Reject stamp
  rejectStamp: async (stampId, accessToken, reason = '') => {
    try {
      console.log('Rejecting stamp:', stampId, 'with reason:', reason);

      if (!accessToken) {
        throw new Error('Access token is required for stamp rejection API');
      }

      const response = await fetchWithTimeout(`https://api.cungvedich.vn/api/redeem/redeem/${stampId}/status`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 3
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stamp rejection response:', data);

        return {
          success: true,
          message: 'Từ chối mã tem thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Stamp rejection error:', errorData);
        throw new Error(`Không thể từ chối mã tem: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Stamp rejection network error:', error);
      throw new Error('Không thể kết nối đến server để từ chối mã tem.');
    }
  },

  // Get product inventory list
  getProductInventory: async (accessToken) => {
    try {
      console.log('Getting product inventory with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for product inventory API');
      }

      const response = await fetchWithTimeout('https://api.cungvedich.vn/api/products/product-inventory', {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Product inventory response:', data);

        // Transform data to match expected format
        // API returns: { statusCode: 200, data: [{ productId, productCode, productName, quantity, extQuantity, ... }] }
        const products = data.data.map(product => ({
          id: product.productId,
          name: product.productName,
          code: product.productCode,
          price: product.productPrice,
          description: product.productDescription,
          imageUrl: product.productImageUrl,
          color: product.productColor,
          ingredient: product.ingredient,
          target: product.target,
          type: product.type,
          age: product.age,
          instruction: product.instruction,
          style: product.style,
          contextOfUse: product.contextOfUse,
          attributes: product.productAttributes,
          stock: product.stock,
          status: product.status,
          categoryId: product.categoryId,
          categoryName: product.categoryName,
          categoryCode: product.categoryCode,
          quantity: product.quantity || 0,
          extQuantity: product.extQuantity || 0,
          notes: product.inventoryNotes,
          createdAt: product.createdAt,
          updatedAt: product.updatedAt
        }));

        return {
          success: true,
          products: products,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get product inventory error:', errorData);
        throw new Error(`Không thể lấy danh sách sản phẩm trong kho: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get product inventory network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách sản phẩm trong kho.');
    }
  },

  // Submit inventory update
  submitInventoryUpdate: async (inventoryData, accessToken) => {
    try {
      console.log('Submitting inventory update with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for inventory update API');
      }

      const response = await fetchWithTimeout('https://api.cungvedich.vn/api/products/product-inventory', {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inventories: inventoryData
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Inventory update response:', data);

        return {
          success: true,
          message: 'Cập nhật kho thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Inventory update error:', errorData);
        throw new Error(`Không thể cập nhật kho: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Inventory update network error:', error);
      throw new Error('Không thể kết nối đến server để cập nhật kho.');
    }
  },

  // Get sales report data for specific month/year
  getSalesReport: async (month, year, accessToken) => {
    try {
      console.log('Getting sales report for month:', month, 'year:', year);

      if (!accessToken) {
        throw new Error('Access token is required for sales report API');
      }

      const response = await fetchWithTimeout(`https://api.cungvedich.vn/api/products/product-sale-report?month=${month}&year=${year}`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Sales report response:', data);

        // Transform data to match expected format
        const salesData = data.data || [];

        return {
          success: true,
          salesData: salesData,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get sales report error:', errorData);
        throw new Error(`Không thể lấy báo cáo doanh số: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get sales report network error:', error);
      throw new Error('Không thể kết nối đến server để lấy báo cáo doanh số.');
    }
  },

  // Get products list (fallback when no sales data exists)
  getProducts: async (accessToken) => {
    try {
      console.log('Getting products list with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for products API');
      }

      const response = await fetchWithTimeout('https://api.cungvedich.vn/api/products?page=1&pageSize=1000', {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Products response:', data);

        // Transform data to match expected format
        const products = data.data?.rows || [];

        return {
          success: true,
          products: products,
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Get products error:', errorData);
        throw new Error(`Không thể lấy danh sách sản phẩm: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Get products network error:', error);
      throw new Error('Không thể kết nối đến server để lấy danh sách sản phẩm.');
    }
  },

  // Submit sales report
  submitSalesReport: async (salesData, accessToken) => {
    try {
      console.log('Submitting sales report with token:', accessToken ? accessToken.substring(0, 20) + '...' : 'None');

      if (!accessToken) {
        throw new Error('Access token is required for sales report submission API');
      }

      const response = await fetchWithTimeout('https://api.cungvedich.vn/api/products/product-sale-report', {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'x-access-token': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(salesData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Sales report submission response:', data);

        return {
          success: true,
          message: 'Lưu báo cáo doanh số thành công',
          data: data
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Sales report submission error:', errorData);
        throw new Error(`Không thể lưu báo cáo doanh số: ${errorData.message || 'Lỗi không xác định'}`);
      }
    } catch (error) {
      console.error('Sales report submission network error:', error);
      throw new Error('Không thể kết nối đến server để lưu báo cáo doanh số.');
    }
  },

  // Format phone number for API
  formatPhoneNumber: (phone) => {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');

    // Ensure it starts with 0 for Vietnamese numbers
    if (cleaned.length === 10 && cleaned.startsWith('0')) {
      return cleaned;
    } else if (cleaned.length === 9) {
      return '0' + cleaned;
    } else if (cleaned.length === 12 && cleaned.startsWith('84')) {
      return '0' + cleaned.substring(2);
    }

    return cleaned;
  }
};

export { CungVeDichAPI };
export default API;
